import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronLeft, ChevronRight, Calendar, ChevronDown, 
  ChevronsLeft, ChevronsRight, X
} from 'lucide-react';
import { 
  format, parseISO, addDays, subDays, addWeeks, subWeeks, 
  addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek,
  endOfWeek, eachDayOfInterval, isSameDay, isSameMonth, isToday
} from 'date-fns';
import './DispatchDateNav.css';

function DispatchDateNav({ selectedDate, onDateChange, compact = false }) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(new Date());
  const pickerRef = useRef(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const currentDate = parseISO(selectedDate);

      switch (e.key) {
        case 'ArrowLeft':
          if (e.shiftKey) {
            onDateChange(format(subWeeks(currentDate, 1), 'yyyy-MM-dd'));
          } else {
            onDateChange(format(subDays(currentDate, 1), 'yyyy-MM-dd'));
          }
          break;
        case 'ArrowRight':
          if (e.shiftKey) {
            onDateChange(format(addWeeks(currentDate, 1), 'yyyy-MM-dd'));
          } else {
            onDateChange(format(addDays(currentDate, 1), 'yyyy-MM-dd'));
          }
          break;
        case 't':
        case 'T':
          if (!e.ctrlKey && !e.metaKey) {
            onDateChange(format(new Date(), 'yyyy-MM-dd'));
          }
          break;
        case 'Escape':
          setShowDatePicker(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedDate, onDateChange]);

  // Navigation functions
  const goToPrevDay = () => {
    onDateChange(format(subDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'));
  };

  const goToNextDay = () => {
    onDateChange(format(addDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'));
  };

  const goToPrevWeek = () => {
    onDateChange(format(subWeeks(parseISO(selectedDate), 1), 'yyyy-MM-dd'));
  };

  const goToNextWeek = () => {
    onDateChange(format(addWeeks(parseISO(selectedDate), 1), 'yyyy-MM-dd'));
  };

  const goToToday = () => {
    onDateChange(format(new Date(), 'yyyy-MM-dd'));
    setShowDatePicker(false);
  };

  const selectDate = (date) => {
    onDateChange(format(date, 'yyyy-MM-dd'));
    setShowDatePicker(false);
  };

  // Generate calendar days for picker
  const getCalendarDays = () => {
    const start = startOfWeek(startOfMonth(pickerMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(pickerMonth), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  };

  // Quick date options
  const quickDates = [
    { label: 'Today', date: new Date() },
    { label: 'Tomorrow', date: addDays(new Date(), 1) },
    { label: 'Next Week', date: addWeeks(new Date(), 1) },
    { label: 'Next Month', date: addMonths(new Date(), 1) },
  ];

  const parsedDate = parseISO(selectedDate);
  const isSelectedToday = isToday(parsedDate);

  return (
    <div className={`dispatch-date-nav ${compact ? 'compact' : ''}`} ref={pickerRef}>
      {/* Week Back */}
      {!compact && (
        <button
          className="nav-btn week-btn"
          onClick={goToPrevWeek}
          title="Previous week (Shift + ←)"
        >
          <ChevronsLeft size={18} />
          <span>Week</span>
        </button>
      )}

      {/* Day Back */}
      <button
        className="nav-btn day-btn"
        onClick={goToPrevDay}
        title="Previous day (←)"
      >
        <ChevronLeft size={compact ? 16 : 20} />
      </button>

      {/* Date Display & Picker Toggle */}
      <button
        className={`date-display ${showDatePicker ? 'active' : ''}`}
        onClick={() => {
          setShowDatePicker(!showDatePicker);
          setPickerMonth(parsedDate);
        }}
      >
        <Calendar size={compact ? 14 : 18} />
        {compact ? (
          <span className="date-main">{format(parsedDate, 'EEE, MMM d')}</span>
        ) : (
          <div className="date-text">
            <span className="date-main">
              {format(parsedDate, 'EEEE, MMMM d')}
            </span>
            <span className="date-year">{format(parsedDate, 'yyyy')}</span>
          </div>
        )}
        {isSelectedToday && <span className="today-badge">Today</span>}
        <ChevronDown size={compact ? 12 : 16} className={`chevron ${showDatePicker ? 'rotated' : ''}`} />
      </button>

      {/* Day Forward */}
      <button
        className="nav-btn day-btn"
        onClick={goToNextDay}
        title="Next day (→)"
      >
        <ChevronRight size={compact ? 16 : 20} />
      </button>

      {/* Week Forward */}
      {!compact && (
        <button
          className="nav-btn week-btn"
          onClick={goToNextWeek}
          title="Next week (Shift + →)"
        >
          <span>Week</span>
          <ChevronsRight size={18} />
        </button>
      )}

      {/* Date Picker Dropdown */}
      {showDatePicker && (
        <div className="date-picker-dropdown">
          <div className="picker-header">
            <button 
              className="picker-nav-btn"
              onClick={() => setPickerMonth(subMonths(pickerMonth, 1))}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="picker-month">
              {format(pickerMonth, 'MMMM yyyy')}
            </span>
            <button 
              className="picker-nav-btn"
              onClick={() => setPickerMonth(addMonths(pickerMonth, 1))}
            >
              <ChevronRight size={18} />
            </button>
            <button 
              className="picker-close-btn"
              onClick={() => setShowDatePicker(false)}
            >
              <X size={16} />
            </button>
          </div>

          <div className="picker-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="picker-days">
            {getCalendarDays().map((day, index) => {
              const isCurrentMonth = isSameMonth(day, pickerMonth);
              const isSelected = isSameDay(day, parsedDate);
              const isDayToday = isToday(day);

              return (
                <button
                  key={index}
                  className={`picker-day ${!isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''} ${isDayToday ? 'today' : ''}`}
                  onClick={() => selectDate(day)}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          <div className="picker-quick-dates">
            {quickDates.map((quick, index) => (
              <button
                key={index}
                className={`quick-date-btn ${isSameDay(quick.date, parsedDate) ? 'active' : ''}`}
                onClick={() => selectDate(quick.date)}
              >
                {quick.label}
              </button>
            ))}
          </div>

          <div className="picker-footer">
            <span className="keyboard-hint">
              ← → to navigate days • Shift + arrows for weeks • T for today
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default DispatchDateNav;
